import { ArboProperty, PropertyV2Attribute } from "../types"

export function discoverAttributes(
  arbo: ArboProperty,
  operation: string,
  type: string,
  subtype: string,
  options: { agentId?: string; clientId?: string } = {}
): PropertyV2Attribute[] {
  const attributes: PropertyV2Attribute[] = []

  // Room attributes
  if (arbo.qtd_quartos && arbo.qtd_quartos > 0) {
    attributes.push({
      key: "bedrooms",
      ref: "bedroom",
      type: "amount",
      label: "Quarto(s)",
      value: arbo.qtd_quartos,
      section: "rooms"
    })
  }

  if (arbo.qtd_banheiro && arbo.qtd_banheiro > 0) {
    attributes.push({
      key: "bathrooms",
      ref: "bathroom",
      type: "amount",
      label: "Banheiro(s)",
      value: arbo.qtd_banheiro,
      section: "rooms"
    })
  }

  if (arbo.qtd_suites && arbo.qtd_suites > 0) {
    attributes.push({
      key: "suites",
      ref: "suites",
      tags: ["has-suite"],
      type: "amount",
      label: "Suíte(s)",
      value: arbo.qtd_suites,
      section: "rooms"
    })
  }

  // Features
  if (arbo.area_total && arbo.area_total > 0) {
    attributes.push({
      key: "total-area",
      ref: "total-area",
      type: "area",
      label: "Área total - m²",
      value: arbo.area_total.toString(),
      section: "feature"
    })
  }

  if (arbo.area_privativa && arbo.area_privativa > 0) {
    attributes.push({
      key: "util-area",
      type: "number",
      label: "Área útil - m²",
      value: arbo.area_privativa.toString(),
      section: "feature"
    })
  }

  if (arbo.qtd_vagas && arbo.qtd_vagas > 0) {
    attributes.push({
      key: "garage",
      ref: "garage",
      type: "amount",
      label: "Vaga(s) na garagem",
      value: arbo.qtd_vagas,
      section: "feature"
    })
  }

  // Process characteristics
  if (arbo.caracteristicas && arbo.caracteristicas.length > 0) {
    const charMap: Record<string, PropertyV2Attribute> = {
      "cozinha": {
        key: "kitchen",
        type: "bool",
        label: "Cozinha",
        value: true,
        section: "rooms"
      },
      "sala de estar": {
        key: "living-room",
        type: "bool",
        label: "Sala de estar",
        value: true,
        section: "rooms"
      },
      "sala de jantar": {
        key: "dinner-room",
        type: "bool",
        label: "Sala de jantar",
        value: true,
        section: "rooms"
      },
      "área de serviço": {
        key: "service-area",
        type: "bool",
        label: "Área de serviço",
        value: true,
        section: "rooms"
      },
      "lavanderia": {
        key: "service-area",
        type: "bool",
        label: "Área de serviço",
        value: true,
        section: "rooms"
      },
      "closet": {
        key: "closet ",
        type: "bool",
        label: "Closet ",
        value: true,
        section: "rooms"
      },
      "sala de tv": {
        key: "tv-room",
        type: "bool",
        label: "Sala de TV",
        value: true,
        section: "rooms"
      },
      "escritório": {
        key: "office",
        type: "bool",
        label: "Escritório",
        value: true,
        section: "rooms"
      },
      "espaço gourmet": {
        key: "gourmet-space",
        type: "bool",
        label: "Espaço Gourmet",
        value: true,
        section: "rooms"
      },
      "área gourmet": {
        key: "gourmet-space",
        type: "bool",
        label: "Espaço Gourmet",
        value: true,
        section: "rooms"
      },
      "varanda": {
        key: "balcony",
        tags: ["balcony"],
        type: "text",
        label: "Sacada",
        value: "padrao",
        section: "rooms",
        "value.label": "Padrão"
      },
      "sacada": {
        key: "balcony",
        tags: ["balcony"],
        type: "text",
        label: "Sacada",
        value: "padrao",
        section: "rooms",
        "value.label": "Padrão"
      },
      "suíte master": {
        key: "master-suite",
        tags: ["suite"],
        type: "bool",
        label: "Suíte Master",
        value: true,
        section: "rooms"
      },
      "piscina": {
        key: "pool",
        type: "bool",
        label: "Piscina",
        value: true,
        section: "improvement"
      },
      "churrasqueira": {
        key: "barbecue",
        tags: ["bbq"],
        type: "bool",
        label: "Churrasqueira",
        value: true,
        section: "improvement"
      },
      "área de lazer": {
        key: "recreation-area",
        type: "bool",
        label: "Área de lazer",
        value: true,
        section: "improvement"
      },
      "playground": {
        key: "playground",
        type: "bool",
        label: "Parquinho",
        value: true,
        section: "improvement"
      },
      "segurança 24h": {
        key: "security-guard",
        tags: ["security"],
        type: "bool",
        label: "Segurança 24h",
        value: true,
        section: "improvement"
      },
      "portaria 24h": {
        key: "security-guard",
        tags: ["security"],
        type: "bool",
        label: "Segurança 24h",
        value: true,
        section: "improvement"
      }
    }

    const addedKeys = new Set<string>()
    
    for (const char of arbo.caracteristicas) {
      const charLower = char.toLowerCase()
      for (const [key, attr] of Object.entries(charMap)) {
        if (charLower.includes(key) && !addedKeys.has(attr.key)) {
          attributes.push(attr)
          addedKeys.add(attr.key)
        }
      }
    }
  }

  // Condominium
  if (arbo.end_condominio || (arbo.valor_condominio && arbo.valor_condominio > 0)) {
    attributes.push({
      key: "in-condominium",
      tags: ["in-condominium"],
      type: "bool",
      label: "Em condomínio",
      value: true,
      section: "improvement"
    })
    
    attributes.push({
      key: "em-condominio",
      value: true
    })
  }

  // Basic attributes
  if (options.agentId) {
    attributes.push({
      key: "agent",
      value: options.agentId
    })
  }

  // Location
  if (arbo.latitude && arbo.longitude) {
    attributes.push({
      key: "position",
      value: `${arbo.latitude},${arbo.longitude}`
    })
  }

  const city = arbo.end_cidade && arbo.end_estado
    ? `${arbo.end_cidade} - ${arbo.end_estado}`
    : arbo.end_cidade || ""
    
  if (city) {
    attributes.push({
      key: "city",
      value: city
    })
  }

  attributes.push(
    { key: "description", value: arbo.descricao || "" },
    { key: "type", value: type },
    { key: "title", value: arbo.titulo || "" },
    { key: "objective", value: "Residencial" },
    { key: "reference", value: arbo.codigo || "" }
  )

  if (options.clientId) {
    attributes.push({
      key: "client",
      value: options.clientId
    })
  }

  attributes.push(
    { key: "model", value: `${subtype.toLowerCase().replace(/ /g, "-")}@1.0` },
    { key: "id", value: `ppt_${generateId()}` },
    { key: "modified_at", value: new Date().toISOString() }
  )

  // Address
  if (arbo.end_logradouro) {
    attributes.push({
      key: "address-street",
      value: arbo.end_logradouro
    })
  }

  // Values
  if (operation === "sale" && arbo.valor_venda) {
    attributes.push(
      { key: "value", value: arbo.valor_venda },
      {
        key: "sale-value",
        ref: "sale-value",
        type: "currency",
        value: arbo.valor_venda
      }
    )
  } else if (operation === "rent" && arbo.valor_aluguel) {
    attributes.push(
      { key: "value", value: arbo.valor_aluguel },
      {
        key: "rent-value",
        ref: "rent-value",
        type: "currency",
        value: arbo.valor_aluguel
      }
    )
  }

  attributes.push(
    { key: "published", value: false },
    { key: "postal-code", value: arbo.end_cep || "" },
    { key: "friendly-url", value: arbo.codigo?.toLowerCase() || "" }
  )

  if (arbo.end_complemento) {
    attributes.push({
      key: "address-complement",
      value: arbo.end_complemento
    })
  }

  if (arbo.end_bairro) {
    attributes.push({
      key: "district",
      value: arbo.end_bairro
    })
  }

  attributes.push(
    { key: "operation", value: operation },
    {
      key: "integrations",
      value: [
        { key: "imovelweb", title: "Imóvel Web" },
        { key: "chavesnamao", title: "Chaves na mão" },
        { key: "procure-imovel", title: "Procure Imóvel" },
        { key: "vivareal", title: "Viva Real" },
        { key: "zapimoveis", title: "Zap Imóveis" },
        { key: "olx", title: "OLX" }
      ]
    }
  )

  // Full address
  const fullAddress = buildFullAddress(arbo)
  if (fullAddress) {
    attributes.push({
      key: "full-address",
      ref: "address",
      type: "address",
      value: fullAddress,
      section: "location"
    })
  }

  attributes.push({
    key: "subtype",
    value: subtype
  })

  return attributes
}

function buildFullAddress(arbo: ArboProperty): string {
  const parts = []
  
  if (arbo.end_logradouro) {
    parts.push(arbo.end_logradouro)
    if (arbo.end_numero) {
      parts[parts.length - 1] += `, ${arbo.end_numero}`
    }
  }
  
  if (arbo.end_complemento) {
    parts.push(arbo.end_complemento)
  }
  
  if (arbo.end_bairro) {
    parts.push(arbo.end_bairro)
  }
  
  const cityState = []
  if (arbo.end_cidade) cityState.push(arbo.end_cidade)
  if (arbo.end_estado) cityState.push(arbo.end_estado)
  
  if (cityState.length > 0) {
    parts.push(cityState.join(" - "))
  }
  
  return parts.join(" - ")
}

function generateId(): string {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789"
  let result = ""
  for (let i = 0; i < 28; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return result
}